import { BACKEND_URL } from '@/config';
﻿import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import {
  Eye, ShieldCheck, ShieldX, X, Package, Navigation, Calendar, Weight,
  MapPin, User, ImageIcon, AlertCircle,
} from 'lucide-react';
import {
  CONDITION_LABELS, TRANSPORT_LABELS,
} from '@/utils/listingParsers';
import { cityFlag as flag } from '@/utils/cityFlag';
import { CCY_SYM, ccySym } from '@/utils/currencyUtils';
import { tripPriceDisplay } from '@/utils/tripPriceUtils';
import { photoCount } from '@/utils/photos';
import { StatusPill } from '@/components/design';

const SHIPMENT_CONSTRAINT_META = {
  fragile:     '🏺 Fragile',      very_fragile: '🔮 Très fragile',
  upright:     '⬆️ Vertical',     no_stack:     '🚫 Ne pas empiler',
  temp:        '🌡️ Temp. contrôlée', cold_chain: '❄️ Chaîne du froid',
  perishable:  '🥗 Périssable',   electronic:   '💻 Électronique',
  valuable:    '💎 Produit de valeur', urgent:   '🚀 Urgent',
  signature:   '✍️ Signature',    insurance:    '🛡️ Assurance',
  bulky:       '📦 Volumineux',   careful:      '🤲 Précaution',
};
const SHIPMENT_CARRIER_META = {
  verified:'✅ Vérifié', professional:'🏢 Entreprise', particular:'👤 Particulier',
  fast:'🚀 Rapide', direct:'🎯 Direct', no_stop:'✈️ Sans escale',
  weekend:'📅 Week-end', evening:'🌙 Soirée', ac_vehicle:'❄️ Climatisé',
  insured:'🛡️ Assuré', fragile_ok:'🏺 Accepte fragile', bulky_ok:'📦 Accepte volumineux',
  french:'🇫🇷 Français', english:'🇬🇧 Anglais', experienced:'⭐ Expérimenté',
};

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const ID_TYPE_LABELS = { passport: 'Passeport', id_card: 'CNI', residence_permit: 'Titre de séjour' };

const fmtDate  = (d) => d ? new Date(d).toLocaleString('fr-FR',  { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtShort = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'short' }) : '—';

// ── Chip helpers ──
const CHIP   = { display:'inline-flex', alignItems:'center', gap:3, padding:'3px 9px', borderRadius:99, fontSize:11, fontWeight:500, background:'#f3f4f6', color:'#374151', border:'1px solid #e5e7eb' };
const CHIP_T = { ...CHIP, background:'#ecfdf5', color:'#065f46', borderColor:'#a7f3d0' };
const CHIP_G = { ...CHIP, background:'#fef9c3', color:'#713f12', borderColor:'#fde047' };
const CHIP_R = { ...CHIP, background:'#fef2f2', color:'#991b1b', borderColor:'#fca5a5' };

/* ─── KYC Review Modal ─── */
function KycReviewModal({ submission, onClose, onAction }) {
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState('');
  if (!submission) return null;
  const u = submission.user || {};
  const doApprove = async () => { setActing('approve'); await onAction('approve', submission.id); setActing(''); };
  const doReject  = async () => { if (!reason.trim()) return; setActing('reject'); await onAction('reject', submission.id, { reason }); setActing(''); };
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth:760, width:'100%' }} data-testid="kyc-review-modal">
        <div className="modal-header">
          <div className="modal-title">Vérification KYC — {u.first_name} {u.last_name}</div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight:'70vh', overflow:'auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16, fontSize:12 }}>
            <div><strong>Email :</strong> {u.email}</div>
            <div><strong>Téléphone :</strong> {u.phone || '·'}</div>
            <div><strong>Type :</strong> {u.profile_type === 'PROFESSIONAL' ? 'Entreprise' : 'Particulier'}</div>
            <div><strong>Document :</strong> {ID_TYPE_LABELS[submission.id_type] || submission.id_type}</div>
            {u.company_name && <div style={{ gridColumn:'1/-1' }}><strong>Société :</strong> {u.company_name}</div>}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14 }}>
            {submission.id_front_data    && <ImgBlock label="Pièce recto"         src={submission.id_front_data}    testid="kyc-img-front" />}
            {submission.id_back_data     && <ImgBlock label="Pièce verso"         src={submission.id_back_data} />}
            {submission.selfie_data      && <ImgBlock label="Selfie"               src={submission.selfie_data}      testid="kyc-img-selfie" />}
            {submission.address_proof_data && <ImgBlock label="Justificatif domicile" src={submission.address_proof_data} />}
          </div>
          {submission.documents_purged_at && (
            <div style={{ fontSize:12, color:'var(--ink2)', marginTop:8 }} data-testid="kyc-documents-purged">
              Pièces supprimées le {new Date(submission.documents_purged_at).toLocaleDateString('fr-FR')}, 30 jours après la décision (conservation limitée, RGPD).
            </div>
          )}
          <div style={{ marginTop:18 }}>
            <label style={{ fontSize:11, color:'var(--ink3)', fontWeight:600, display:'block', marginBottom:6 }}>Motif de rejet (requis pour rejeter)</label>
            <textarea className="field-textarea" rows={2} placeholder="Ex: document illisible, photo périmée…" value={reason} onChange={e => setReason(e.target.value)} data-testid="kyc-reject-reason" />
          </div>
        </div>
        <div className="modal-footer" style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
          <button className="btn btn-danger" disabled={!reason.trim() || acting==='reject'} onClick={doReject} data-testid="btn-kyc-reject"><ShieldX size={14} /> {acting==='reject' ? 'Rejet…' : 'Rejeter'}</button>
          <button className="btn btn-teal"  disabled={acting==='approve'} onClick={doApprove} data-testid="btn-kyc-approve"><ShieldCheck size={14} /> {acting==='approve' ? 'Validation…' : 'Approuver'}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Shipment Review Modal ─── */
function ShipmentReviewModal({ item, onClose, onAction }) {
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState('');
  if (!item) return null;
  const o            = item.owner || {};
  const constraints  = Array.isArray(item.constraints)         ? item.constraints         : [];
  const carrierPrefs = Array.isArray(item.carrier_preferences) ? item.carrier_preferences : [];
  const _rawCatsShp  = Array.isArray(item.categories)          ? item.categories          : [];
  const categories   = _rawCatsShp.length > 1 ? _rawCatsShp.filter(c => c !== 'Autre') : _rawCatsShp;
  const currencies   = Array.isArray(item.currencies)          ? item.currencies          : [];
  const primaryCcy   = item.primary_currency || 'EUR';
  const sym          = ccySym(primaryCcy);

  const doApprove = async () => { setActing('approve'); await onAction('approve', item.id); setActing(''); };
  const doReject  = async () => { if (!reason.trim()) return; setActing('reject');  await onAction('reject',  item.id, { reason }); setActing(''); };
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth:720, width:'100%' }} data-testid="shipment-review-modal">
        <div className="modal-header">
          <div className="modal-title">Modération annonce — {item.title}</div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight:'70vh', overflow:'auto' }}>

          {/* Infos principales */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
            <InfoLine icon={<MapPin size={14}/>}   label="Départ"   value={`${item.departure_city}${item.departure_country ? ` (${item.departure_country})` : ''}`} />
            <InfoLine icon={<MapPin size={14}/>}   label="Arrivée"  value={`${item.arrival_city}${item.arrival_country ? ` (${item.arrival_country})` : ''}`} />
            <InfoLine icon={<Calendar size={14}/>} label="Date"     value={fmtShort(item.departure_date)} />
            <InfoLine icon={<Weight size={14}/>}   label="Poids"    value={`${item.weight_kg} kg`} />
            <InfoLine icon={<Package size={14}/>}  label="Dimensions" value={item.dimensions || '·'} />
            <InfoLine icon={<span style={{fontWeight:700}}>{sym}</span>} label="Budget" value={item.price_offered ? `${item.price_offered} ${sym}` : 'Budget libre'} />
            {currencies.length > 0 && (
              <InfoLine icon={<span>💱</span>} label="Devises" value={currencies.map(c => c === primaryCcy ? `★ ${c}` : c).join(', ')} />
            )}
            {constraints.includes('urgent') && <InfoLine icon={<span>🚀</span>} label="Urgence" value="Livraison urgente" />}
          </div>

          {/* Description */}
          {item.description && (
            <SectionBox label="Description">
              <div style={{ fontSize:13, color:'var(--ink)', lineHeight:1.5 }}>{item.description}</div>
            </SectionBox>
          )}

          {/* Catégories */}
          {categories.length > 0 && (
            <SectionBox label="Catégories">
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {categories.map(c => <span key={c} style={CHIP}>{c}</span>)}
                {item.category_custom && <span style={CHIP}>📝 {item.category_custom}</span>}
              </div>
            </SectionBox>
          )}

          {/* Contraintes */}
          {(constraints.length > 0 || item.constraint_custom) && (
            <SectionBox label="Contraintes de transport">
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {constraints.filter(id => id !== 'other').map(id => {
                  const label = SHIPMENT_CONSTRAINT_META[id];
                  return label ? <span key={id} style={CHIP}>{label}</span> : null;
                })}
                {item.constraint_custom && <span style={CHIP}>📋 {item.constraint_custom}</span>}
              </div>
            </SectionBox>
          )}

          {/* Préférences transporteur */}
          {(carrierPrefs.length > 0 || item.message_to_carriers) && (
            <SectionBox label="Préférences transporteur">
              {carrierPrefs.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {carrierPrefs.map(id => {
                    const label = SHIPMENT_CARRIER_META[id];
                    return label ? <span key={id} style={CHIP_T}>{label}</span> : null;
                  })}
                </div>
              )}
              {item.message_to_carriers && (
                <div style={{ marginTop:8, fontSize:12, color:'var(--ink2)', fontStyle:'italic' }}>« {item.message_to_carriers} »</div>
              )}
            </SectionBox>
          )}

          {/* Photos */}
          {Array.isArray(item.photos) && item.photos.length > 0 && (
            <SectionBox label={`Photos (${item.photos.length})`} icon={<ImageIcon size={12}/>}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                {item.photos.map((p, i) => <img key={i} src={p} alt={`photo-${i}`} style={{ width:'100%', borderRadius:8, border:'1px solid var(--bdl)', aspectRatio:'1/1', objectFit:'cover' }} />)}
              </div>
            </SectionBox>
          )}

          {/* Expéditeur */}
          {o.first_name && (
            <SectionBox label="Expéditeur" icon={<User size={12}/>}>
              <div style={{ fontSize:13, fontWeight:600 }}>{o.first_name} {o.last_name} <span style={{ fontSize:11, color:'var(--ink3)', fontWeight:400 }}>· {o.email}</span></div>
              <div style={{ fontSize:11, color:'var(--ink3)', marginTop:2 }}>
                {o.profile_type === 'PROFESSIONAL' ? '🏢 Entreprise' : '👤 Particulier'}
                {o.is_verified && ' · ✓ Vérifié'}
                {o.avg_rating  ? ` · ⭐ ${o.avg_rating.toFixed(1)} (${o.rating_count || 0})` : ''}
              </div>
            </SectionBox>
          )}

          {/* Motif rejet */}
          <div style={{ marginTop:14 }}>
            <label style={{ fontSize:11, color:'var(--ink3)', fontWeight:600, display:'block', marginBottom:6 }}>Motif de rejet (requis pour rejeter)</label>
            <textarea className="field-textarea" rows={2} placeholder="Ex: contenu inapproprié, infos insuffisantes…" value={reason} onChange={e => setReason(e.target.value)} data-testid="shipment-reject-reason" />
          </div>
        </div>
        <div className="modal-footer" style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
          <button className="btn btn-danger" disabled={!reason.trim() || acting==='reject'} onClick={doReject} data-testid="btn-shipment-reject"><ShieldX size={14} /> {acting==='reject' ? 'Rejet…' : 'Rejeter'}</button>
          <button className="btn btn-teal"  disabled={acting==='approve'} onClick={doApprove} data-testid="btn-shipment-approve"><ShieldCheck size={14} /> {acting==='approve' ? 'Validation…' : 'Approuver'}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Trip Review Modal ─── */
function TripReviewModal({ item, onClose, onAction }) {
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState('');
  if (!item) return null;
  const o    = item.owner || {};
  const priceInfo = tripPriceDisplay(item);

  // Champs structurés du trajet (N4 : plus d'ancien format encodé dans la description)
  const transportDisplay = item.transport_modes?.length > 0
    ? item.transport_modes.map(t => TRANSPORT_LABELS[t] || t).join(', ')
    : null;
  const maxPerPkg   = item.max_weight_per_parcel ?? null;
  const maxParcels  = item.max_parcels ?? null;
  const arrivalDate = item.arrival_date
    ? new Date(item.arrival_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const FLEX_LABELS = { pm3j: '± 3 jours', pm1s: '± 1 semaine', pm2s: '± 2 semaines', fixe: 'Date fixe' };
  const flexDisplay  = item.flex_departure && item.flex_departure !== 'fixe' ? FLEX_LABELS[item.flex_departure] : null;
  const categories = (() => {
    const raw = item.accepted_categories || [];
    return raw.length > 1 ? raw.filter(c => c !== 'Autre') : raw;
  })();
  const conditions = item.conditions || [];
  const isPrixFerme = item.counter_offers === false;
  const msgToSender = item.message_to_senders || null;
  const allCurrencies = item.currencies || [];
  const tripPrimaryCcy = item.primary_currency || 'EUR';
  const deviseDisplay = allCurrencies.length > 0 && !(allCurrencies.length === 1 && allCurrencies[0] === 'EUR')
    ? allCurrencies.map(c => c === tripPrimaryCcy ? `★ ${c}` : c).join(', ')
    : null;
  const noteLibre = item.description && item.description !== `Trajet ${item.departure_city} → ${item.arrival_city}` ? item.description : null;

  const doApprove = async () => { setActing('approve'); await onAction('approve', item.id); setActing(''); };
  const doReject  = async () => { if (!reason.trim()) return; setActing('reject');  await onAction('reject',  item.id, { reason }); setActing(''); };
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth:660, width:'100%' }} data-testid="trip-review-modal">
        <div className="modal-header">
          <div className="modal-title">Modération trajet — {item.departure_city} → {item.arrival_city}</div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight:'70vh', overflow:'auto' }}>

          {/* Infos principales */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
            <InfoLine icon={<MapPin size={14}/>}   label="Départ"    value={item.departure_city} />
            <InfoLine icon={<MapPin size={14}/>}   label="Arrivée"   value={item.arrival_city} />
            <InfoLine icon={<Calendar size={14}/>} label="Date départ" value={fmtShort(item.departure_date)} />
            <InfoLine icon={<Weight size={14}/>}   label="Capacité"  value={`${item.available_weight_kg} kg`} />
            <InfoLine icon={<span style={{fontWeight:700}}>€</span>} label={priceInfo.label} value={priceInfo.display} />
            {transportDisplay && <InfoLine icon={<span>✈️</span>} label="Transport" value={transportDisplay} />}
            {maxPerPkg   != null && <InfoLine icon={<Package size={14}/>}  label="Max / colis"       value={`${maxPerPkg} kg`} />}
            {maxParcels  != null && <InfoLine icon={<Package size={14}/>}  label="Nbre colis max"    value={String(maxParcels)} />}
            {arrivalDate         && <InfoLine icon={<Calendar size={14}/>} label="Arrivée estimée"   value={arrivalDate} />}
            {flexDisplay         && <InfoLine icon={<span>📅</span>}       label="Flexibilité départ" value={flexDisplay} />}
            {deviseDisplay       && <InfoLine icon={<span>💱</span>}       label="Devises acceptées"  value={deviseDisplay} />}
          </div>

          {/* Note libre du transporteur */}
          {noteLibre && (
            <SectionBox label="Description du trajet">
              <div style={{ fontSize:13, color:'var(--ink)', lineHeight:1.5 }}>{noteLibre}</div>
            </SectionBox>
          )}

          {/* Catégories acceptées */}
          {categories.length > 0 && (
            <SectionBox label="Catégories acceptées">
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {categories.map(c => <span key={c} style={CHIP}>{c}</span>)}
              </div>
            </SectionBox>
          )}

          {/* Conditions d'acceptation */}
          {(conditions.length > 0 || isPrixFerme) && (
            <SectionBox label="Conditions d'acceptation">
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {conditions.map(c => <span key={c} style={CHIP_T}>{CONDITION_LABELS[c] || c}</span>)}
                {isPrixFerme && <span style={CHIP}>🔒 Prix ferme</span>}
              </div>
            </SectionBox>
          )}

          {/* Message du transporteur */}
          {msgToSender && (
            <SectionBox label="Message aux expéditeurs">
              <div style={{ fontSize:12, color:'var(--ink2)', fontStyle:'italic' }}>« {msgToSender} »</div>
            </SectionBox>
          )}

          {/* Transporteur */}
          {o.first_name && (
            <SectionBox label="Transporteur" icon={<User size={12}/>}>
              <div style={{ fontSize:13, fontWeight:600 }}>{o.first_name} {o.last_name} <span style={{ fontSize:11, color:'var(--ink3)', fontWeight:400 }}>· {o.email}</span></div>
              <div style={{ fontSize:11, color:'var(--ink3)', marginTop:2 }}>
                {o.profile_type === 'PROFESSIONAL' ? '🏢 Entreprise' : '👤 Particulier'}
                {o.is_verified && ' · ✓ Vérifié'}
                {o.avg_rating  ? ` · ⭐ ${o.avg_rating.toFixed(1)} (${o.rating_count || 0})` : ''}
              </div>
            </SectionBox>
          )}

          {/* Motif rejet */}
          <div style={{ marginTop:14 }}>
            <label style={{ fontSize:11, color:'var(--ink3)', fontWeight:600, display:'block', marginBottom:6 }}>Motif de rejet (requis pour rejeter)</label>
            <textarea className="field-textarea" rows={2} placeholder="Ex: itinéraire incohérent, prix abusif…" value={reason} onChange={e => setReason(e.target.value)} data-testid="trip-reject-reason" />
          </div>
        </div>
        <div className="modal-footer" style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
          <button className="btn btn-danger" disabled={!reason.trim() || acting==='reject'} onClick={doReject} data-testid="btn-trip-reject"><ShieldX size={14} /> {acting==='reject' ? 'Rejet…' : 'Rejeter'}</button>
          <button className="btn btn-teal"  disabled={acting==='approve'} onClick={doApprove} data-testid="btn-trip-approve"><ShieldCheck size={14} /> {acting==='approve' ? 'Validation…' : 'Approuver'}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Shared subcomponents ─── */
function InfoLine({ icon, label, value }) {
  return (
    <div>
      <div style={{ fontSize:10.5, color:'var(--ink3)', fontWeight:600, marginBottom:3, display:'flex', alignItems:'center', gap:4 }}>{icon} {label}</div>
      <div style={{ fontSize:13, color:'var(--ink)', fontWeight:500 }}>{value || '·'}</div>
    </div>
  );
}

function SectionBox({ label, icon, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:11, color:'var(--ink3)', fontWeight:600, marginBottom:6, display:'flex', alignItems:'center', gap:5 }}>
        {icon} {label}
      </div>
      <div style={{ padding:'10px 12px', background:'var(--cream,#faf9f6)', borderRadius:8, border:'1px solid var(--border,#e6e9ef)' }}>
        {children}
      </div>
    </div>
  );
}

function ImgBlock({ label, src, testid }) {
  return (
    <div>
      <div style={{ fontSize:11, color:'var(--ink3)', marginBottom:6, fontWeight:600 }}>{label}</div>
      <img src={src} alt={label} style={{ width:'100%', borderRadius:8, border:'1px solid var(--bdl)' }} data-testid={testid} />
    </div>
  );
}

/* ─── Main page ─── */
export default function AdminModeration() {
  const navigate = useNavigate();
  const toast = useToast();
  const [shipmentsQueue, setShipmentsQueue] = useState([]);
  const [tripsQueue,     setTripsQueue]     = useState([]);
  const [shipmentsTotal, setShipmentsTotal] = useState(0);
  const [tripsTotal,     setTripsTotal]     = useState(0);
  const [kycList,   setKycList]   = useState([]);
  const [missions,  setMissions]  = useState([]);
  const [tab, setTab] = useState('shipments');
  const [loading, setLoading] = useState(true);
  const [reviewKyc,      setReviewKyc]      = useState(null);
  const [reviewShipment, setReviewShipment] = useState(null);
  const [reviewTrip,     setReviewTrip]     = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sQ, tQ, k, m] = await Promise.all([
        axios.get(`${API}/admin/shipments/queue?limit=100`),
        axios.get(`${API}/admin/trips/queue?limit=100`),
        axios.get(`${API}/users/kyc/queue?status=PENDING_REVIEW&limit=30`),
        axios.get(`${API}/admin/missions?limit=20`),
      ]);
      setShipmentsQueue(sQ.data.data || []);
      setTripsQueue(tQ.data.data || []);
      setShipmentsTotal(sQ.data.meta?.total || 0);
      setTripsTotal(tQ.data.meta?.total || 0);
      setKycList(k.data.data || []);
      setMissions(m.data.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openKyc = async (id) => { try { const r = await axios.get(`${API}/users/kyc/${id}`); setReviewKyc(r.data); } catch { toast.error('Erreur de chargement.'); } };
  const handleKycAction = async (action, id, data = {}) => {
    try {
      if (action === 'approve') await axios.patch(`${API}/users/kyc/${id}/approve`);
      else await axios.patch(`${API}/users/kyc/${id}/reject`, data);
      toast.success(action === 'approve' ? 'KYC approuvé.' : 'KYC rejeté.');
      setReviewKyc(null); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };
  const handleShipmentAction = async (action, id, data = {}) => {
    try {
      const r = await axios.post(`${API}/admin/shipments/${id}/${action}`, action === 'approve' ? {} : data);
      toast.success(r.data?.message || 'Action effectuée.');
      setReviewShipment(null); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };
  const handleTripAction = async (action, id, data = {}) => {
    try {
      const r = await axios.post(`${API}/admin/trips/${id}/${action}`, action === 'approve' ? {} : data);
      toast.success(r.data?.message || 'Action effectuée.');
      setReviewTrip(null); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const tabs = [
    ['shipments', 'Annonces',  shipmentsTotal],
    ['trips',     'Trajets',   tripsTotal],
    ['kyc',       'KYC',       kycList.length],
    ['missions',  'Missions',  null],
  ];

  return (
    <div data-testid="admin-moderation-page">
      <div style={{ marginBottom:24 }}>
        <div className="c2s-topbar-title">Modération</div>
        <div className="c2s-topbar-sub">Annonces, trajets et KYC en attente de validation</div>
      </div>

      <div style={{ display:'flex', gap:2, marginBottom:18, background:'rgba(15,23,42,.06)', borderRadius:8, padding:4, width:'fit-content', flexWrap:'wrap' }}>
        {tabs.map(([v, l, count]) => (
          <button key={v} data-testid={`admin-tab-${v}`}
            style={{ padding:'6px 14px', borderRadius:6, border:'none', fontWeight:500, fontSize:12.5, cursor:'pointer', background: tab===v ? '#fff' : 'transparent', color: tab===v ? '#111827' : '#6B7280', fontFamily:'var(--f-body)', display:'inline-flex', alignItems:'center', gap:6 }}
            onClick={() => setTab(v)}>
            {l}
            {count != null && count > 0 && (
              <span style={{ background: tab===v ? '#C8832A' : 'rgba(17,24,39,.18)', color:'#fff', fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:999 }}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="skeleton" style={{ height:200, borderRadius:14 }} />
      ) : tab === 'shipments' ? (
        shipmentsQueue.length === 0
          ? <EmptyState icon={<Package size={48}/>} title="Aucune annonce en attente" sub="Toutes les annonces sont à jour." />
          : <div className="mlc-grid-2" data-testid="moderation-shipments-grid">
              {shipmentsQueue.map(s => (
                <ModerationCard key={s.id} testid={`mod-ship-${s.id}`}
                  icon={<Package size={14}/>} tag="Colis"
                  title={s.title} route={<>{flag(s.departure_city)} {s.departure_city} → {flag(s.arrival_city)} {s.arrival_city}</>}
                  meta={[
                    { label:`${s.weight_kg} kg` },
                    { label:fmtShort(s.departure_date) },
                    { label: s.price_offered ? `${s.price_offered}€` : 'Budget libre' },
                    ...(photoCount(s) ? [{ label:`${photoCount(s)} photo${photoCount(s)>1?'s':''}` }] : []),
                    ...(s.constraints?.includes('urgent') ? [{ label:'🚀 Urgent' }] : []),
                  ]}
                  owner={s.owner} created={s.created_at}
                  onReview={() => setReviewShipment(s)}
                />
              ))}
            </div>
      ) : tab === 'trips' ? (
        tripsQueue.length === 0
          ? <EmptyState icon={<Navigation size={48}/>} title="Aucun trajet en attente" sub="Tous les trajets sont à jour." />
          : <div className="mlc-grid-2" data-testid="moderation-trips-grid">
              {tripsQueue.map(t => {
                const transportLabel = t.transport_modes?.length > 0
                  ? TRANSPORT_LABELS[t.transport_modes[0]] || t.transport_modes[0]
                  : null;
                return (
                  <ModerationCard key={t.id} testid={`mod-trip-${t.id}`}
                    icon={<Navigation size={14}/>} tag="Trajet"
                    title={<>{flag(t.departure_city)} {t.departure_city} → {flag(t.arrival_city)} {t.arrival_city}</>}
                    route={fmtShort(t.departure_date)}
                    meta={[
                      { label:`${t.available_weight_kg} kg dispo` },
                      { label: tripPriceDisplay(t).display },
                      ...(transportLabel ? [{ label: transportLabel }] : []),
                    ]}
                    owner={t.owner} created={t.created_at}
                    onReview={() => setReviewTrip(t)}
                  />
                );
              })}
            </div>
      ) : tab === 'kyc' ? (
        kycList.length === 0
          ? <EmptyState icon={<ShieldCheck size={48}/>} title="Aucun KYC en attente" sub="Tous les utilisateurs sont vérifiés." />
          : <div className="card" style={{ padding:0 }}>
              <table className="c2s-table">
                <thead><tr><th>Utilisateur</th><th>Email</th><th>Type</th><th>Document</th><th>Soumis</th><th>Actions</th></tr></thead>
                <tbody>
                  {kycList.map(k => {
                    const u = k.user || {};
                    return (
                      <tr key={k.id}>
                        <td style={{ fontWeight:600 }}>{u.first_name} {u.last_name}</td>
                        <td style={{ fontSize:12 }}>{u.email}</td>
                        <td>{u.profile_type==='PROFESSIONAL' ? '🏢 Entreprise' : '👤 Particulier'}</td>
                        <td>{ID_TYPE_LABELS[k.id_type] || k.id_type}</td>
                        <td style={{ fontSize:11, color:'var(--ink3)' }}>{fmtDate(k.submitted_at)}</td>
                        <td><button className="btn btn-primary btn-sm" data-testid={`btn-review-kyc-${k.id}`} onClick={() => openKyc(k.id)}><Eye size={12}/> Examiner</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
      ) : (
        <div className="card" style={{ padding:0 }}>
          <table className="c2s-table">
            <thead><tr><th>ID</th><th>Trajet</th><th>Statut</th><th>Montant</th><th>Date</th><th>Actions</th></tr></thead>
            <tbody>
              {missions.map(m => (
                <tr key={m.id}>
                  <td style={{ fontSize:12, fontFamily:'monospace' }}>#{m.id.slice(-8)}</td>
                  <td>{m.shipment?.departure_city||'-'} → {m.shipment?.arrival_city||'-'}</td>
                  <td><StatusPill domain="mission" code={m.status} /></td>
                  <td style={{ fontWeight:600 }}>{m.contracted_price?.toFixed(2)}€</td>
                  <td style={{ fontSize:12, color:'var(--ink3)' }}>{fmtShort(m.created_at)}</td>
                  <td><button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={() => navigate(`/app/missions/${m.id}`)}><Eye size={12}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <KycReviewModal      submission={reviewKyc}      onClose={() => setReviewKyc(null)}      onAction={handleKycAction} />
      <ShipmentReviewModal item={reviewShipment}        onClose={() => setReviewShipment(null)} onAction={handleShipmentAction} />
      <TripReviewModal     item={reviewTrip}            onClose={() => setReviewTrip(null)}     onAction={handleTripAction} />
    </div>
  );
}

function ModerationCard({ testid, icon, tag, title, route, meta=[], owner, created, onReview }) {
  return (
    <div className="mlc" data-testid={testid} onClick={onReview} role="button" tabIndex={0}
      onKeyDown={(e) => (e.key==='Enter'||e.key===' ') && onReview()}>
      <div className="mlc-head">
        <div className="mlc-route-wrap">
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:3, padding:'3px 8px', borderRadius:10, fontSize:10, fontWeight:500, background: tag==='Colis'?'#FDF0D0':'#E6F5F2', color: tag==='Colis'?'#92580F':'#075044' }}>
              {icon} {tag}
            </span>
            <span style={{ fontSize:10.5, color:'#6B7280' }}>{fmtDate(created)}</span>
          </div>
          <h3 className="mlc-route" style={{ fontSize:17 }}>{title}</h3>
          <div className="mlc-sub">{route}</div>
        </div>
        <span className="mlc-badge mlc-badge--blue" style={{ display:'inline-flex', alignItems:'center', gap:4 }}><AlertCircle size={11}/> En attente</span>
      </div>
      {meta.length > 0 && (
        <div className="mlc-chips">
          {meta.map((m, i) => <span key={i} className="mlc-chip">{m.label}</span>)}
        </div>
      )}
      <div className="mlc-foot">
        <div style={{ fontSize:12, color:'#374151', display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
          <User size={13}/>
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {owner ? `${owner.first_name||''} ${owner.last_name||''}` : 'Utilisateur inconnu'}
            {owner?.is_verified && ' ✓'}
          </span>
        </div>
        <button type="button" className="mlc-icn mlc-icn--primary"
          onClick={(e) => { e.stopPropagation(); onReview(); }} aria-label="Examiner" data-testid={`${testid}-review`}>
          <Eye size={16}/>
        </button>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-sub">{sub}</div>
    </div>
  );
}
